import React, { useState, useEffect } from "react";

/**
 * Debug Panel - Shows sync status between audio and highlighting
 * Helps diagnose desynchronization issues
 */
export default function DebugPanel({ mediaRef, currentPlayingWord, words, lastWordIndexRef }) {
  const [debugInfo, setDebugInfo] = useState({
    audioTime: 0,
    wordStart: null,
    wordEnd: null,
    wordText: null,
    wordIndex: null,
    cachedIndex: null,
    delta: null,
    jumpType: null,
    searchMethod: null,
    updateCount: 0
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const mediaElement = mediaRef.current;
      if (!mediaElement) return;
      
      const audioTime = mediaElement.currentTime;
      const currentWord = words.find(w => w.index === currentPlayingWord);
      const cachedIdx = lastWordIndexRef?.current;
      
      // Calculate jump type and search method
      const lastPlaybackTime = mediaElement.getAttribute('data-last-time') || '0';
      const timeDiff = Math.abs(audioTime - parseFloat(lastPlaybackTime));
      const isBigJump = timeDiff > 5;
      const isHugeJump = timeDiff > 60;
      
      let jumpType = 'Normal';
      let searchMethod = 'Linear';
      
      if (isHugeJump) {
        jumpType = 'HUGE';
        searchMethod = 'ClosestWord';
      } else if (isBigJump) {
        jumpType = 'BIG';
        searchMethod = 'Binary+Closest';
      }
      
      // Calculate better delta (distance to word center or closest edge)
      let delta = 'N/A';
      if (currentWord) {
        if (audioTime >= currentWord.start && audioTime <= currentWord.end) {
          // Inside word - distance to center
          const wordCenter = (currentWord.start + currentWord.end) / 2;
          delta = (audioTime - wordCenter).toFixed(3);
        } else if (audioTime < currentWord.start) {
          // Before word
          delta = (currentWord.start - audioTime).toFixed(3);
        } else {
          // After word
          delta = (audioTime - currentWord.end).toFixed(3);
        }
      }
      
      setDebugInfo({
        audioTime: audioTime.toFixed(3),
        wordStart: currentWord?.start?.toFixed(3) || 'N/A',
        wordEnd: currentWord?.end?.toFixed(3) || 'N/A',
        wordText: currentWord?.word || 'None',
        wordIndex: currentWord?.index ?? 'N/A',
        cachedIndex: cachedIdx ?? 'N/A',
        delta: delta,
        jumpType: jumpType,
        searchMethod: searchMethod,
        updateCount: Date.now()
      });
    }, 100); // Update every 100ms

    return () => clearInterval(interval);
  }, [mediaRef, currentPlayingWord, words, lastWordIndexRef]);

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      background: 'rgba(0,0,0,0.85)',
      color: '#0f0',
      padding: '15px',
      borderRadius: '8px',
      fontFamily: 'monospace',
      fontSize: '12px',
      zIndex: 9999,
      minWidth: '300px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
    }}>
      <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#0ff' }}>
        🔍 SYNC DEBUG PANEL
      </div>
      <div>Audio Time: <span style={{color: '#ff0'}}>{debugInfo.audioTime}s</span></div>
      <div>Word: <span style={{color: '#fff'}}>"{debugInfo.wordText}"</span></div>
      <div>Word Index: <span style={{color: '#0ff'}}>{debugInfo.wordIndex}</span> (Cached: {debugInfo.cachedIndex})</div>
      <div>Word Start: <span style={{color: '#0f0'}}>{debugInfo.wordStart}s</span></div>
      <div>Word End: <span style={{color: '#0f0'}}>{debugInfo.wordEnd}s</span></div>
      <div>
        Delta: <span style={{
          color: Math.abs(parseFloat(debugInfo.delta || 0)) > 0.3 ? '#f00' : '#0f0'
        }}>
          {debugInfo.delta}s
        </span>
        {Math.abs(parseFloat(debugInfo.delta || 0)) > 0.3 && 
          <span style={{color: '#f00'}}> ⚠ OUT OF SYNC!</span>
        }
      </div>
      <div>Jump Type: <span style={{color: debugInfo.jumpType === 'HUGE' ? '#f80' : debugInfo.jumpType === 'BIG' ? '#ff0' : '#0f0'}}>{debugInfo.jumpType}</span></div>
      <div>Search Method: <span style={{color: '#0ff'}}>{debugInfo.searchMethod}</span></div>
      <div style={{marginTop: '10px', fontSize: '10px', color: '#888'}}>
        Updates: {debugInfo.updateCount}
      </div>
      <div style={{marginTop: '10px', fontSize: '10px', color: '#aaa'}}>
        💡 Delta should be 0.0-0.3s (distance to word)
      </div>
      <div style={{marginTop: '10px', fontSize: '10px', color: '#aaa'}}>
        💡 HUGE = 1+ min jump, BIG = 5s-60s, Normal = 0-5s
      </div>
      <div style={{marginTop: '10px', fontSize: '10px', color: '#aaa'}}>
        🔧 Direct timestamp search - no estimation needed!
      </div>
    </div>
  );
}

